import { Dayjs } from 'dayjs';
import {
  createPortfolio,
  createPortfolioFromTemplate,
} from 'lib/api/integrations/digitalSignature';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { PortfolioTemplate, PortfolioValidation } from 'lib/types/models';
import { SignatureType } from 'lib/types/models';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { PendingDocument } from './Inputs/DocumentsDropzone';

export function useCreateTemplate(
  template: PortfolioTemplate | undefined,
  businessIdentifier: string | undefined,
  formValues: Record<string, unknown>,
) {
  const dispatch = useDispatch();
  const isValid = useMemo(() => {
    const fieldsValid = Object.values(formValues).every(
      (value) => value !== '',
    );
    const templateValid = template?.id !== '';
    const bussinessValid = businessIdentifier !== '';
    return fieldsValid && templateValid && bussinessValid;
  }, [formValues, businessIdentifier, template]);

  const handleCreateTemplate = async () => {
    try {
      if (!isValid || !template) {
        showAlert(dispatch, 'error', 'Invalid form values');
        return;
      }
      const body = {
        templateId: template.id,
        businessIdentifier: businessIdentifier,
        formValues: formValues,
      };
      await createPortfolioFromTemplate(dispatch, body);
      showAlert(dispatch, 'success', 'Portfolio created successfully');
    } catch (error) {
      console.error('Error creating portfolio:', error);
    }
  };

  return { handleCreateTemplate };
}

export function useCreatePortfolio(
  businessIdentifier: string | undefined,
  documents: PendingDocument[],
  title: string,
  validUntil: Dayjs | null,
  selectedValidations: number[],
  validations: PortfolioValidation[],
  ownUserIds: number[],
  businessUserIds: number[],
) {
  const dispatch = useDispatch();
  const isValid = useMemo(() => {
    return !!businessIdentifier && businessIdentifier.trim() !== '';
  }, [businessIdentifier]);

  const handleCreatePortfolio = async () => {
    try {
      if (!isValid || !businessIdentifier) {
        showAlert(dispatch, 'error', 'Invalid form values');
        return;
      }

      const fd = new FormData();

      documents.forEach((d) => {
        fd.append('files', d.file);
      });
      const filesInfo = documents.map((d) => ({
        description: d.description,
        signature: d.signature as SignatureType,
      }));
      fd.append('filesInfo', JSON.stringify(filesInfo));

      fd.append('ownUsers', JSON.stringify(ownUserIds));
      fd.append('businessUsers', JSON.stringify(businessUserIds));
      fd.append('businessIdentifier', businessIdentifier);
      fd.append('title', title);

      if (validUntil) {
        fd.append('validUntil', validUntil.format('YYYY-MM-DD'));
      }

      if (selectedValidations.length > 0) {
        const selectedTypes = selectedValidations
          .map((id) => validations.find((v) => v.id === id)?.type)
          .filter((t): t is string => Boolean(t));
        if (selectedTypes.length > 0) {
          fd.append('validations', JSON.stringify(selectedTypes));
        }
      }
      await createPortfolio(dispatch, fd);
      showAlert(dispatch, 'success', 'Portafolio creado exitosamente');
    } catch (error) {
      errorHandler(dispatch, error);
    }
  };

  return { handleCreatePortfolio };
}
